import faceImage from '$src/assets/images/face.png'
import {
  Button,
  Center,
  Group,
  Image,
  Paper,
  PinInput,
  rem,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconArrowLeft } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { trpcClient } from '../../lib/trpc'
import { authHooks } from '../../lib/trpcHooks'
import { useAuthStore } from '../../store/useAuthStore'
import { getAcquisitionDataFromStorage } from '../../utils/utm'

interface OtpStepProps {
  email: string
  onBack: () => void
  onRetry: () => void
}

export function OtpStep({ email, onBack, onRetry }: OtpStepProps) {
  const otpInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { setUser } = useAuthStore()

  const form = useForm({
    initialValues: {
      token: '',
    },
    validate: {
      token: (value) =>
        value.length === 6 ? null : 'Koodin tulee olla 6 numeroa',
    },
  })

  const verifyOtp = authHooks.useVerifyOtp()

  const handleVerify = async () => {
    const validation = form.validate()
    if (validation.hasErrors) return

    try {
      await verifyOtp.mutateAsync({
        email,
        token: form.values.token,
        app: 'resident',
        acquisitionData: getAcquisitionDataFromStorage(),
      })
      // Auth successful - fetch user data to populate auth store
      const userData = await trpcClient.user.me.query()
      setUser(userData)
      // Navigate to home page after successful login
      navigate({ to: '/home' })
    } catch {
      // Error handled by trpcHooks
      form.setFieldValue('token', '')
    }
  }

  useEffect(() => {
    // Focus on OTP input when component mounts
    setTimeout(() => {
      otpInputRef.current?.focus()
    }, 300)
  }, [])

  return (
    <Paper
      radius="xl"
      p="xl"
      withBorder
      shadow="xl"
      style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        width: '100%',
      }}
    >
      <Stack>
        <Group justify="center" mb="md">
          <Image
            src={faceImage}
            alt="Markku"
            style={{
              width: rem(80),
              height: rem(80),
              objectFit: 'contain',
            }}
          />
        </Group>

        <Title order={3} ta="center" mb="xs">
          Syötä vahvistuskoodi
        </Title>
        <Text size="sm" c="dimmed" ta="center" mb="xs">
          Lähetimme 6-numeroisen koodin osoitteeseen:
        </Text>
        <Text size="sm" fw={500} ta="center" mb="lg" c="brandOrange.6">
          {email}
        </Text>

        <Center mb="xl">
          <PinInput
            length={6}
            type="number"
            oneTimeCode
            value={form.values.token}
            onChange={(value) => form.setFieldValue('token', value)}
            onComplete={handleVerify}
            ref={otpInputRef}
            error={!!form.errors.token}
            aria-label="Kirjautumiskoodi"
            styles={{
              input: {
                fontSize: rem(20),
                fontWeight: 600,
                width: rem(42),
                height: rem(48),
              },
            }}
          />
        </Center>

        <Group justify="space-between" mt="xl">
          <Button
            size="lg"
            radius="xl"
            variant="subtle"
            onClick={onBack}
            leftSection={<IconArrowLeft size={20} />}
          >
            Takaisin
          </Button>
          <Button
            size="lg"
            radius="xl"
            color="brandOrange"
            onClick={handleVerify}
            loading={verifyOtp.isPending}
            disabled={form.values.token.length !== 6}
          >
            Kirjaudu
          </Button>
        </Group>

        <Center mt="md">
          <Button variant="subtle" size="sm" onClick={onRetry} c="brandBlue.6">
            Lähetä koodi uudelleen
          </Button>
        </Center>
      </Stack>
    </Paper>
  )
}
